/* eslint-disable no-underscore-dangle */
import * as React from "react";

import { PortableText } from "@portabletext/react";
import { graphql } from "gatsby";

import {
  Container,
  Content,
  Columns,
  Column,
  List,
  Item,
  Link,
  Copyright,
  Links,
  Branding,
  BrandingInfo,
} from "./Footer.styles";
import Text from "cms/components/Text";
import Button from "cms/components/Button";
import LogoIcon from "cms/components/LogoIcon";
import Badge from "cms/components/Badge";

export interface FooterProps extends Queries.SanityFooter {}

type ListType = {
  headline: string;
  items: Queries.SanityFooterLink[];
};

type ColumnType = {
  key: string;
  lists: ListType[];
};

// Using eval is bad, but we need to support javascript: links,
// and would like to avoid having React warnings about it.
// Also, we trust the content from the CMS system.
const JsEnabledLink = ({
  children,
  href,
  ...props
}: React.HTMLProps<HTMLAnchorElement>) => {
  // eslint-disable-next-line no-script-url
  const finalHref = href?.startsWith("javascript:") ? "#" : href;

  const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // eslint-disable-next-line no-script-url
    if (href?.startsWith("javascript:")) {
      event.preventDefault();
      event.stopPropagation();

      // eslint-disable-next-line no-script-url
      const js = href.replace("javascript:", "");

      // eslint-disable-next-line no-eval
      eval(js);
    }
  };

  return (
    <Link href={finalHref} {...props} onClick={onClick}>
      {children}
    </Link>
  );
};

const Footer: React.FC<FooterProps> = ({ copyright, columns }) => {
  // Transform CMS data into proper columns and lists for easier rendering
  const uiColumns: ColumnType[] = (columns ?? []).map((column) => ({
    key: column?._key ?? "",
    lists: (column?.links ?? []).reduce((acc, rawLink) => {
      if (rawLink?._type === "footerHeadline") {
        const link = rawLink as Queries.SanityFooterHeadline;
        acc.push({
          headline: link.headline ?? "",
          items: [],
        });
      }

      if (rawLink?._type === "footerLink") {
        acc[acc.length - 1].items.push(rawLink as Queries.SanityFooterLink);
      }

      return acc;
    }, [] as ListType[]),
  }));

  return (
    <Container>
      <Content>
        <Branding>
          <BrandingInfo>
            <Link href="/">
              <LogoIcon variant="embossed" size="3.5rem" />
            </Link>

            <div>
              <Text
                variant="L1"
                weight="medium"
                as="p"
                color="activated"
                marginBottom="0.5rem"
              >
                Uizard
              </Text>
              <Text variant="B4" as="p">
                Visualize product ideas fast and easy with AI
              </Text>
            </div>
          </BrandingInfo>

          <Link href="https://app.uizard.io/sign-up/">
            <Button label="Sign up for free" variant="primary" size="small" />
          </Link>
        </Branding>

        <Links>
          <Columns>
            {uiColumns.map((column) => (
              <Column key={column.key}>
                {column.lists.map((list) => (
                  <React.Fragment key={list.headline}>
                    <Text variant="L3" weight="medium" as="p" color="activated">
                      {list.headline}
                    </Text>

                    <List>
                      {list.items.map((item) => (
                        <Item key={item._key}>
                          <JsEnabledLink
                            href={item.url ?? undefined}
                            target={item.target ?? undefined}
                          >
                            {item.text}
                            {item.badge && (
                              <Badge
                                variant={
                                  item.variant === "magic" ? "magic" : "primary"
                                }
                                text={item.badge}
                              />
                            )}
                          </JsEnabledLink>
                        </Item>
                      ))}
                    </List>
                  </React.Fragment>
                ))}
              </Column>
            ))}
          </Columns>
        </Links>

        <Copyright>
          2024 © Uizard Technologies. All rights reserved.
          {/* <PortableText
            // @ts-expect-error Mismatch in graphql and portable text types
            value={copyright}
          /> */}
        </Copyright>
      </Content>
    </Container>
  );
};

export const query = graphql`
  fragment Footer on SanityFooter {
    _key
    copyright {
      _key
      _type
      children {
        _key
        _type
        marks
        text
      }
    }
    columns {
      _key

      links {
        ...on SanityFooterHeadline {
          _key
          _type
          headline
        }

        ...on SanityFooterLink {
          _key
          _type
          text
          url
          target
          badge
          variant
        }
      }
    }
  }
`;

export default Footer;
